import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../../components/navbar';
import toast from 'react-hot-toast';
import { FaSave, FaTrash, FaPlus, FaMinus, FaArrowUp, FaArrowDown } from 'react-icons/fa';

// Generate unique IDs for slides
const generateId = () => Math.random().toString(36).substr(2, 9);

const LandingPageEditor = () => {
  const [landingPage, setLandingPage] = useState({
    bannerSlides: [{ 
      id: generateId(), 
      image: {}, 
      titleLine1: '', 
      titleLine2: '', 
      titleLine3: '', 
      description: '', 
      primaryButtonText: '', 
      secondaryButtonText: '' 
    }],
    services: { 
      header: { subtitle: '', title: '' },
      serviceItems: [
        { 
          id: generateId(),
          title: '', 
          image: {}, 
          accordionItems: [{ 
            id: generateId(),
            title: '', 
            description: '' 
          }], 
          description: '', 
          link: '', 
          type: '' 
        }
      ] 
    },
    whyChooseUs: { 
      header: { subtitle: '', title: '' },
      featureCards: [{ 
        id: generateId(),
        title: '', 
        description: '', 
        image: {} 
      }]
    },
    aboutUs: { 
      header: { subtitle: '', title: '' },
      storySection: { image: {}, description: '' } 
    },
    testimonials: { 
      header: { subtitle: '', title: '' },
      testimonialCards: [{ 
        id: generateId(),
        image: {}, 
        name: '', 
        quote: '', 
        location: '' 
      }] 
    },
    contactUs: { 
      header: { subtitle: '', title: '' } 
    },
    footer: { tagline: '', email: '', phone: '', address: '' }
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imagePreviews, setImagePreviews] = useState({});
  const [expandedSections, setExpandedSections] = useState([]); // Changed to empty array
  const [saving, setSaving] = useState(false);
  const [imageFiles, setImageFiles] = useState({});

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch landing page content
  useEffect(() => {
    const fetchLandingPage = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/landing-page`);
        const data = response.data;
        
        // Helper to add IDs to items when missing
        const addIds = (items) => {
          return items?.map(item => ({
            ...item,
            id: item.id || generateId(),
            ...(item.accordionItems ? {
              accordionItems: item.accordionItems.map(ai => ({
                ...ai,
                id: ai.id || generateId()
              }))
            } : {})
          })) || [];
        };
        
        // Fix malformed serviceItems if needed
        const safeServiceItems = Array.isArray(data.services?.serviceItems) 
          ? data.services.serviceItems 
          : [];
        
        const processedData = {
          bannerSlides: addIds(data.bannerSlides) || [],
          services: {
            header: data.services?.header || { subtitle: '', title: '' },
            serviceItems: addIds(safeServiceItems) || []
          },
          whyChooseUs: {
            header: data.whyChooseUs?.header || { subtitle: '', title: '' },
            featureCards: addIds(data.whyChooseUs?.featureCards) || []
          },
          aboutUs: {
            header: data.aboutUs?.header || { subtitle: '', title: '' },
            storySection: data.aboutUs?.storySection || { image: {}, description: '' }
          },
          testimonials: {
            header: data.testimonials?.header || { subtitle: '', title: '' },
            testimonialCards: addIds(data.testimonials?.testimonialCards) || []
          },
          contactUs: {
            header: data.contactUs?.header || { subtitle: '', title: '' }
          },
          footer: data.footer || { tagline: '', email: '', phone: '', address: '' }
        };
        
        setLandingPage(processedData);
      } catch (err) {
        if (err.response) {
          console.error("Server response error:", err.response.data);
        } else if (err.request) {
          console.error("No response received:", err.request);
        }
        setError('Error fetching landing page content');
      } finally {
        setLoading(false);
      }
    };

    fetchLandingPage();
  }, []);

  // Toggle section expansion
  const toggleSection = (section) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  // Handle image preview
  const handleImageChange = (e, fieldPath) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews({
          ...imagePreviews,
          [fieldPath]: reader.result
        });
      };
      reader.readAsDataURL(file);
      
      // Store file object in state
      setImageFiles(prev => ({
        ...prev,
        [fieldPath]: file
      }));
    }
  };

  // Handle text input changes
  const handleTextChange = (e, path) => {
    const paths = path.split('.');
    setLandingPage(prev => {
      const newState = JSON.parse(JSON.stringify(prev));
      let current = newState;
      
      for (let i = 0; i < paths.length - 1; i++) {
        if (!current[paths[i]]) {
          if (!isNaN(paths[i+1])) {
            current[paths[i]] = [];
          } else {
            current[paths[i]] = {};
          }
        }
        current = current[paths[i]];
      }
      
      const lastKey = paths[paths.length - 1];
      current[lastKey] = e.target.value;
      return newState;
    });
  };

  // Handle accordion item changes
  const handleAccordionChange = (e, serviceIndex, itemIndex, field) => {
    setLandingPage(prev => {
      const newState = JSON.parse(JSON.stringify(prev));
      if (
        newState.services.serviceItems[serviceIndex] &&
        newState.services.serviceItems[serviceIndex].accordionItems &&
        newState.services.serviceItems[serviceIndex].accordionItems[itemIndex]
      ) {
        newState.services.serviceItems[serviceIndex].accordionItems[itemIndex][field] = e.target.value;
      }
      return newState;
    });
  };

  // Add new item to an array
  const addArrayItem = (path, defaultValue) => {
    setLandingPage(prev => {
      const newState = JSON.parse(JSON.stringify(prev));
      const paths = path.split('.');
      let current = newState;
      
      // Traverse to the parent of the array
      for (let i = 0; i < paths.length - 1; i++) {
        current = current[paths[i]];
      }
      
      // Get the array property name
      const arrayKey = paths[paths.length - 1];
      
      // Ensure the target is an array
      if (!Array.isArray(current[arrayKey])) {
        current[arrayKey] = [];
      }
      
      // Add new item with unique ID
      current[arrayKey].push({
        ...defaultValue,
        id: generateId(),
        // Add IDs to nested items
        ...(defaultValue.accordionItems ? {
          accordionItems: defaultValue.accordionItems.map(ai => ({
            ...ai,
            id: generateId()
          }))
        } : {})
      });
      return newState;
    });
  };

  // Remove item from an array
  const removeArrayItem = (path, index) => {
    setLandingPage(prev => {
      const newState = JSON.parse(JSON.stringify(prev));
      const paths = path.split('.');
      let current = newState;
      
      for (let i = 0; i < paths.length - 1; i++) {
        current = current[paths[i]];
      }
      
      // Get the array property name
      const arrayKey = paths[paths.length - 1];
      
      if (Array.isArray(current[arrayKey]) && current[arrayKey].length > 1) {
        current[arrayKey].splice(index, 1);
      }
      return newState;
    });
  };

  // Move item in an array
  const moveArrayItem = (path, index, direction) => {
    if (index === 0 && direction === 'up') return;
    
    setLandingPage(prev => {
      const newState = JSON.parse(JSON.stringify(prev));
      const paths = path.split('.');
      let current = newState;
      
      for (let i = 0; i < paths.length - 1; i++) {
        current = current[paths[i]];
      }
      
      // Get the array property name
      const arrayKey = paths[paths.length - 1];
      
      if (Array.isArray(current[arrayKey]) && current[arrayKey].length > 1) {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < current[arrayKey].length) {
          [current[arrayKey][index], current[arrayKey][newIndex]] = 
          [current[arrayKey][newIndex], current[arrayKey][index]];
        }
      }
      return newState;
    });
  };

  // Save changes
  const saveChanges = async () => {
    try {

    setSaving(true);

        const formData = new FormData();
        
        // Create a clean copy of landingPage data
        const cleanLandingPage = JSON.parse(JSON.stringify(landingPage));
        
        // Remove image URLs before sending to backend
        const removeUrls = (obj) => {
          if (Array.isArray(obj)) {
            return obj.map(removeUrls);
          }
          if (obj && typeof obj === 'object') {
            const newObj = { ...obj };
            if (newObj.image && newObj.image.url) {
              delete newObj.image.url;
            }
            for (const key in newObj) {
              newObj[key] = removeUrls(newObj[key]);
            }
            return newObj;
          }
          return obj;
        };
        
        const cleanedData = removeUrls(cleanLandingPage);
        formData.append('landingPageData', JSON.stringify(cleanedData));
        
        // Append image files
        const uploadedFields = [];
        Object.keys(imageFiles).forEach(fieldPath => {
          if (imageFiles[fieldPath]) {
            formData.append(fieldPath, imageFiles[fieldPath]);
            uploadedFields.push(fieldPath);
          }
        });

        await axios.put(`${API_URL}/api/landing-page`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

            const newImageFiles = {...imageFiles};
            uploadedFields.forEach(field => {
              delete newImageFiles[field];
            });
            setImageFiles(newImageFiles);
      
      // Refetch updated data
      const refetchResponse = await axios.get(`${API_URL}/api/landing-page`);
      const refetchedData = refetchResponse.data;
      
      // Add IDs to items when missing
      const addIds = (items) => {
        return items?.map(item => ({
          ...item,
          id: item.id || generateId(),
          ...(item.accordionItems ? {
            accordionItems: item.accordionItems.map(ai => ({
              ...ai,
              id: ai.id || generateId()
            }))
          } : {})
        })) || [];
      };
      
      // Fix malformed serviceItems if needed
      const safeServiceItems = Array.isArray(refetchedData.services?.serviceItems) 
        ? refetchedData.services.serviceItems 
        : [];
      
      // Process refetched data to add missing IDs
      const processedData = {
        bannerSlides: addIds(refetchedData.bannerSlides) || [],
        services: {
          header: refetchedData.services?.header || {},
          serviceItems: addIds(safeServiceItems) || []
        },
        whyChooseUs: {
          header: refetchedData.whyChooseUs?.header || {},
          featureCards: addIds(refetchedData.whyChooseUs?.featureCards) || []
        },
        aboutUs: {
          header: refetchedData.aboutUs?.header || {},
          storySection: refetchedData.aboutUs?.storySection || {}
        },
        testimonials: {
          header: refetchedData.testimonials?.header || {},
          testimonialCards: addIds(refetchedData.testimonials?.testimonialCards) || []
        },
        contactUs: {
          header: refetchedData.contactUs?.header || {}
        },
        footer: refetchedData.footer || {}
      };
      
      setLandingPage(processedData);
      
      // Clear ONLY previews for images that were uploaded
      const newImagePreviews = {...imagePreviews};
      uploadedFields.forEach(field => {
        delete newImagePreviews[field];
      });
      setImagePreviews(newImagePreviews);

      setSaving(false);      
      
      toast.success('Landing page updated successfully!');
    } catch (err) {
      setSaving(false);
      console.error('Save error:', err);
      toast.error('Failed to update landing page');
    }
  };

  // Render editable field
  const renderEditableField = (label, value, path, type = 'text', placeholder = '') => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        {type === 'textarea' ? (
          <textarea
            value={value || ''}
            onChange={(e) => handleTextChange(e, path)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={placeholder}
            rows={4}
          />
        ) : (
          <input
            type={type}
            value={value || ''}
            onChange={(e) => handleTextChange(e, path)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={placeholder}
          />
        )}
      </div>
    );
  };

  // Render image field
  const renderImageField = (label, image, path, id) => {
    const preview = imagePreviews[path] || (image?.url || '');
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        {preview && (
          <div className="mb-2">
            <img 
              src={preview} 
              alt="Preview" 
              className="max-w-full h-40 object-contain border rounded"
            />
          </div>
        )}
        <input
          type="file"
          name={path}
          onChange={(e) => handleImageChange(e, path)}
          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
      </div>
    );
  };

  // Render section
  const renderSection = (title, sectionKey, content) => {
    const isExpanded = expandedSections.includes(sectionKey);
    return (
      <div className="border border-gray-200 rounded-lg mb-4">
        <button
          className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100"
          onClick={() => toggleSection(sectionKey)}
        >
          <h3 className="font-semibold text-lg">{title}</h3>
          <span>{isExpanded ? <FaMinus /> : <FaPlus />}</span>
        </button>
        
        {isExpanded && (
          <div className="p-4 bg-white">
            {content}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="p-6 pt-28 max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Landing Page Editor</h1>
            {/* <button
              onClick={saveChanges}
              disabled={saving}
              className={`flex items-center gap-2 px-6 py-3 rounded-md ${
                saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FaSave /> Save All Changes
                </>
              )}
            </button> */}
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Banner Section */}
        {renderSection("Banner Section", "banner", (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900">Banner Slides</h4>
              <button
                type="button"
                onClick={() => addArrayItem('bannerSlides', { 
                  image: {}, 
                  titleLine1: '', 
                  titleLine2: '', 
                  titleLine3: '', 
                  description: '', 
                  primaryButtonText: '', 
                  secondaryButtonText: '' 
                })}
                className="flex items-center gap-1 text-sm bg-green-600 text-white px-3 py-1 rounded"
              >
                <FaPlus /> Add Slide
              </button>
            </div>
            
            {landingPage.bannerSlides.map((slide, index) => (
              <div key={slide.id} className="mb-6 p-4 border border-gray-200 rounded-lg relative">
                <div className="absolute top-2 right-2 flex gap-2">
                  {index > 0 && (
                    <button
                      onClick={() => moveArrayItem('bannerSlides', index, 'up')}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Move up"
                    >
                      <FaArrowUp />
                    </button>
                  )}
                  {index < landingPage.bannerSlides.length - 1 && (
                    <button
                      onClick={() => moveArrayItem('bannerSlides', index, 'down')}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Move down"
                    >
                      <FaArrowDown />
                    </button>
                  )}
                  {landingPage.bannerSlides.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('bannerSlides', index)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Remove slide"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
                
                <h5 className="font-medium mb-3">Slide {index + 1}</h5>
                
                {renderImageField(
                  "Background Image", 
                  slide.image, 
                  `banner-${slide.id}`
                )}
                
                {renderEditableField(
                  "Title Line 1", 
                  slide.titleLine1 || '', 
                  `bannerSlides.${index}.titleLine1`,
                  'text',
                  'First line of title'
                )}
                
                {renderEditableField(
                  "Title Line 2", 
                  slide.titleLine2 || '', 
                  `bannerSlides.${index}.titleLine2`,
                  'text',
                  'Second line of title'
                )}
                
                {renderEditableField(
                  "Title Line 3", 
                  slide.titleLine3 || '', 
                  `bannerSlides.${index}.titleLine3`,
                  'text',
                  'Third line of title'
                )}
                
                {renderEditableField(
                  "Description", 
                  slide.description || '', 
                  `bannerSlides.${index}.description`,
                  'textarea',
                  'Banner description'
                )}
                
                {renderEditableField(
                  "Primary Button Text", 
                  slide.primaryButtonText || '', 
                  `bannerSlides.${index}.primaryButtonText`,
                  'text',
                  'Button text'
                )}
                
                {renderEditableField(
                  "Secondary Button Text", 
                  slide.secondaryButtonText || '', 
                  `bannerSlides.${index}.secondaryButtonText`,
                  'text',
                  'Optional second button'
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Services Section */}
        {renderSection("Services Section", "services", (
          <div>
            {/* Services Header */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4">Section Header</h4>
              
              {renderEditableField(
                "Title", 
                landingPage.services.header?.title || '', 
                'services.header.title',
                'text',
                'OUR SERVICES'
              )}              
              
              {renderEditableField(
                "Subtitle", 
                landingPage.services.header?.subtitle || '', 
                'services.header.subtitle',
                'text',
                'Explore what we offer.'
              )}
            </div>
            
            {/* Service Items */}
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900">Service Items</h4>
              <button
                type="button"
                onClick={() => addArrayItem('services.serviceItems', { 
                  title: '', 
                  image: {}, 
                  accordionItems: [{ title: '', description: '' }], 
                  description: '', 
                  link: '', 
                  type: '' 
                })}
                className="flex items-center gap-1 text-sm bg-green-600 text-white px-3 py-1 rounded"
              >
                <FaPlus /> Add Service
              </button>
            </div>
            
            {landingPage.services.serviceItems.map((service, index) => (
              <div key={service.id} className="mb-6 p-4 border border-gray-200 rounded-lg relative">
                <div className="absolute top-2 right-2 flex gap-2">
                  {index > 0 && (
                    <button
                      onClick={() => moveArrayItem('services.serviceItems', index, 'up')}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Move up"
                    >
                      <FaArrowUp />
                    </button>
                  )}
                  {index < landingPage.services.serviceItems.length - 1 && (
                    <button
                      onClick={() => moveArrayItem('services.serviceItems', index, 'down')}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Move down"
                    >
                      <FaArrowDown />
                    </button>
                  )}
                  {landingPage.services.serviceItems.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('services.serviceItems', index)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Remove service"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
                
                <h5 className="font-medium mb-3">Service {index + 1}</h5>
                
                {renderImageField(
                  "Service Image", 
                  service.image, 
                  `service-${service.id}`
                )}
                
                {renderEditableField(
                  "Title", 
                  service.title || '', 
                  `services.serviceItems.${index}.title`,
                  'text',
                  'Service title'
                )}
                
                {/* Accordion Items */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accordion Items
                  </label>
                  
                  {(service.accordionItems || []).map((item, itemIndex) => (
                    <div key={item.id} className="mb-3 p-3 border border-gray-200 rounded-md relative">
                      <div className="absolute top-2 right-2">
                        {service.accordionItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setLandingPage(prev => {
                                const newState = JSON.parse(JSON.stringify(prev));
                                newState.services.serviceItems[index].accordionItems.splice(itemIndex, 1);
                                return newState;
                              });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                      
                      <input
                        type="text"
                        value={item.title || ''}
                        onChange={(e) => handleAccordionChange(e, index, itemIndex, 'title')}
                        className="w-full px-2 py-1 mb-2 border border-gray-300 rounded"
                        placeholder="Title"
                      />
                      
                      <textarea
                        value={item.description || ''}
                        onChange={(e) => handleAccordionChange(e, index, itemIndex, 'description')}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                        placeholder="Description"
                        rows={2}
                      />
                    </div>
                  ))}
                  
                  <button
                    onClick={() => {
                      setLandingPage(prev => {
                        const newState = JSON.parse(JSON.stringify(prev));
                        if (!newState.services.serviceItems[index].accordionItems) {
                          newState.services.serviceItems[index].accordionItems = [];
                        }
                        newState.services.serviceItems[index].accordionItems.push({
                          id: generateId(),
                          title: '',
                          description: ''
                        });
                        return newState;
                      });
                    }}
                    className='flex items-center gap-1 text-sm bg-green-600 text-white px-3 py-1 rounded'
                  >
                    <FaPlus size={12} /> Add Accordion Item
                  </button>
                </div>
                
                {renderEditableField(
                  "Description", 
                  service.description || '', 
                  `services.serviceItems.${index}.description`,
                  'textarea',
                  'Service description (for non-inspection services)'
                )}
                
                {renderEditableField(
                  "Link", 
                  service.link || '', 
                  `services.serviceItems.${index}.link`,
                  'text',
                  'Navigation link'
                )}
                
                {renderEditableField(
                  "Type", 
                  service.type || '', 
                  `services.serviceItems.${index}.type`,
                  'text',
                  'Service type'
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Why Choose Us Section */}
        {renderSection("Why Choose Us", "whyChooseUs", (
          <div>
            {/* Section Header */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4">Section Header</h4>
              
              {renderEditableField(
                "Title", 
                landingPage.whyChooseUs.header?.title || '', 
                'whyChooseUs.header.title',
                'text',
                'WHY CHOOSE US'
              )}              
              
              {renderEditableField(
                "Subtitle", 
                landingPage.whyChooseUs.header?.subtitle || '', 
                'whyChooseUs.header.subtitle',
                'text',
                'An Automotive Solution Initiative by Industry Experts'
              )}
            </div>
            
            {/* Feature Cards */}
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900">Feature Cards</h4>
              <button
                type="button"
                onClick={() => addArrayItem('whyChooseUs.featureCards', { 
                  title: '', 
                  description: '', 
                  image: {} 
                })}
                className="flex items-center gap-1 text-sm bg-green-600 text-white px-3 py-1 rounded"
              >
                <FaPlus /> Add Card
              </button>
            </div>
            
            {landingPage.whyChooseUs.featureCards.map((card, index) => (
              <div key={card.id} className="mb-6 p-4 border border-gray-200 rounded-lg relative">
                <div className="absolute top-2 right-2 flex gap-2">
                  {index > 0 && (
                    <button
                      onClick={() => moveArrayItem('whyChooseUs.featureCards', index, 'up')}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Move up"
                    >
                      <FaArrowUp />
                    </button>
                  )}
                  {index < landingPage.whyChooseUs.featureCards.length - 1 && (
                    <button
                      onClick={() => moveArrayItem('whyChooseUs.featureCards', index, 'down')}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Move down"
                    >
                      <FaArrowDown />
                    </button>
                  )}
                  {landingPage.whyChooseUs.featureCards.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('whyChooseUs.featureCards', index)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Remove card"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
                
                <h5 className="font-medium mb-3">Card {index + 1}</h5>
                
                {renderImageField(
                  "Card Image", 
                  card.image, 
                  `feature-${card.id}`
                )}
                
                {renderEditableField(
                  "Title", 
                  card.title || '', 
                  `whyChooseUs.featureCards.${index}.title`,
                  'text',
                  'Card title'
                )}
                
                {renderEditableField(
                  "Description", 
                  card.description || '', 
                  `whyChooseUs.featureCards.${index}.description`,
                  'textarea',
                  'Card description'
                )}
              </div>
            ))}
          </div>
        ))}

        {/* About Us Section */}
        {renderSection("About Us", "aboutUs", (
          <div>
            {/* Section Header */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4">Section Header</h4>
              
              {renderEditableField(
                "Title", 
                landingPage.aboutUs.header?.title || '', 
                'aboutUs.header.title',
                'text',
                'ABOUT US'
              )}              
              
              {renderEditableField(
                "Subtitle", 
                landingPage.aboutUs.header?.subtitle || '', 
                'aboutUs.header.subtitle',
                'text',
                'Empowering Your Drive'
              )}
            </div>
            
            {/* Story Section */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4">Story Section</h4>
              
              {renderImageField(
                "Story Image", 
                landingPage.aboutUs.storySection?.image, 
                'story-image'
              )}
              
              {renderEditableField(
                "Description", 
                landingPage.aboutUs.storySection?.description || '', 
                'aboutUs.storySection.description',
                'textarea',
                'Our Comprehensive Inspection covers 3500+ Vehicle checkpoints...'
              )}
            </div>
          </div>
        ))}

        {/* Testimonials Section */}
        {renderSection("Testimonials", "testimonials", (
          <div>
            {/* Section Header */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4">Section Header</h4>
              
              {renderEditableField(
                "Title", 
                landingPage.testimonials.header?.title || '', 
                'testimonials.header.title',
                'text',
                'REVIEWS'
              )}              
              
              {renderEditableField(
                "Subtitle", 
                landingPage.testimonials.header?.subtitle || '', 
                'testimonials.header.subtitle',
                'text',
                'Testimonials'
              )}
            </div>
            
            {/* Testimonial Cards - Fixed array handling */}
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900">Testimonial Cards</h4>
              <button
                type="button"
                onClick={() => addArrayItem('testimonials.testimonialCards', {
                  image: {},
                  name: '',
                  quote: '',
                  location: '',
                })}
                className="flex items-center gap-1 text-sm bg-green-600 text-white px-3 py-1 rounded"
              >
                <FaPlus /> Add Testimonial
              </button>
            </div>

            {landingPage.testimonials.testimonialCards?.map((testimonial, index) => (
              <div key={testimonial.id} className="mb-6 p-4 border border-gray-200 rounded-lg relative">
                <div className="absolute top-2 right-2 flex gap-2">
                  {index > 0 && (
                    <button
                      onClick={() => moveArrayItem('testimonials.testimonialCards', index, 'up')}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Move up"
                    >
                      <FaArrowUp />
                    </button>
                  )}
                  {index < landingPage.testimonials.testimonialCards.length - 1 && (
                    <button
                      onClick={() => moveArrayItem('testimonials.testimonialCards', index, 'down')}
                      className="p-1 text-gray-600 hover:text-gray-900"
                      title="Move down"
                    >
                      <FaArrowDown />
                    </button>
                  )}
                  {landingPage.testimonials.testimonialCards.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('testimonials.testimonialCards', index)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Remove testimonial"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>

                <h5 className="font-medium mb-3">Testimonial {index + 1}</h5>

                {renderImageField(
                  'Testimonial Image',
                  testimonial.image,
                  `testimonial-${testimonial.id}`
                )}

                {renderEditableField(
                  'Name',
                  testimonial.name || '',
                  `testimonials.testimonialCards.${index}.name`,
                  'text',
                  'Sophie Carter'
                )}

                {renderEditableField(
                  'Quote',
                  testimonial.quote || '',
                  `testimonials.testimonialCards.${index}.quote`,
                  'textarea',
                  'Working with your design team was an absolute pleasure...'
                )}

                {renderEditableField(
                  'Location',
                  testimonial.location || '',
                  `testimonials.testimonialCards.${index}.location`,
                  'text',
                  'New York, USA'
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Contact Us Section */}
        {renderSection("Contact Us", "contactUs", (
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-4">Section Header</h4>

            {renderEditableField(
              "Title", 
              landingPage.contactUs.header?.title || '', 
              'contactUs.header.title',
              'text',
              'CONTACT US'
            )}            
            
            {renderEditableField(
              "Subtitle", 
              landingPage.contactUs.header?.subtitle || '', 
              'contactUs.header.subtitle',
              'text',
              'How you can reach us'
            )}
          </div>
        ))}

        {/* Footer Section */}
        {renderSection("Footer", "footer", (
          <div className="p-4 border border-gray-200 rounded-lg">
            {renderEditableField(
              "Tagline", 
              landingPage.footer.tagline || '', 
              'footer.tagline',
              'textarea',
              'Your trusted partner for expert vehicle inspections...'
            )}
            
            {renderEditableField(
              "Email", 
              landingPage.footer.email || '', 
              'footer.email',
              'email',
              'info@carvetpro.com'
            )}
            
            {renderEditableField(
              "Phone", 
              landingPage.footer.phone || '', 
              'footer.phone',
              'tel',
              '+91 96115 60076'
            )}
            
            {renderEditableField(
              "Address", 
              landingPage.footer.address || '', 
              'footer.address',
              'text',
              'Bangalore, India 560100'
            )}
          </div>
        ))}

        <div className="mt-8 flex justify-end">
          <button
            onClick={saveChanges}
            disabled={saving}
            className={`flex items-center gap-2 px-6 py-3 rounded-md ${
              saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <FaSave /> Save All Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPageEditor;